import type { GlobalStore } from '../../../state/zustand';
import type {
  ScenarioLocation,
  ScenarioOrder,
} from '../../../lib/planningData';
import type { LocationId, PortCallData, PortCallEntry } from '../../core/Core';
import {
  getIntegrationPortCosts,
  type PortCostByVcMaps,
} from './portCostIntegration';
import {
  getDefaultPortCostByType,
  type PortCostEstimateMaps,
} from '../../hooks/usePortCostEstimates';
import {
  getLatestHistoricalPortCost,
  type LatestHistoricalPortCostEstimates,
} from './portCostLatestHistory';
import type { PortCostByCoaMaps } from '../../hooks/usePortCostCoa';
import type { DefaultMap } from '@seaber/common/lib/collections';
import type { ClientId } from '../../core/ClientId';
import type { Currency, ScenarioTimeline_ShipFragment } from '../../../graphql';
import type { VoyageGroup } from '../../core/Timelines';
import {
  isPortCallArrivalEventTimeData,
  isTCOrderEventTimeData,
  type Events,
} from '../../core/TimeData';
import type { DefaultPortCostEstimates } from './portCostDefault';
import { getPortCostDefaults } from './portCostDefault';
import type { HistoricalPortCostEstimates } from './portCostHistory';
import {
  getHistoricalPortCostEstimates,
  mapExternalPortCostEstimatesToParams,
} from './portCostHistory';
import type { CostParam, CostsByPortCall } from './costs';
import { type PortCostPredictionByPortCall } from './portCostPrediction';
import { type MarcuraPortCosts } from '../../marcura/context';

type ShipmentsByPortCallId = DefaultMap<ClientId | null, Set<ClientId | null>>;

export function getPortCosts({
  currencyRates,
  baseCurrency,
  locationById,
  portCalls,
  portCostByVcMaps,
  portCostEstimateMaps,
  portCostByCoaMaps,
  portCostMarcuraMaps,
  portCostPredictions,
  shipmentsByPortCallId,
  vessel,
}: {
  currencyRates: GlobalStore['currencyRates'];
  baseCurrency: Currency;
  locationById: Map<LocationId, ScenarioLocation>;
  portCalls: PortCallData[];
  portCostByVcMaps: PortCostByVcMaps;
  portCostEstimateMaps: PortCostEstimateMaps;
  portCostByCoaMaps: PortCostByCoaMaps;
  portCostMarcuraMaps: MarcuraPortCosts;
  portCostPredictions: PortCostPredictionByPortCall | null;
  shipmentsByPortCallId: ShipmentsByPortCallId;
  vessel: ScenarioTimeline_ShipFragment;
  voyageGroups: VoyageGroup<Events>[];
}): CostsByPortCall {
  const costs: CostsByPortCall = new Map();
  const vesselName = vessel.name;
  if (!vesselName) return costs;

  const ports = new Set<string>();
  for (const pc of portCalls) {
    const name = locationById.get(pc.locationId)?.name;
    if (name) {
      ports.add(name);
    }
  }

  for (const pc of portCalls) {
    const arrivalEventTd = pc.tds.find(isPortCallArrivalEventTimeData);
    const tcTimeData = pc.tds.filter(isTCOrderEventTimeData);
    const orders = tcTimeData.map((entry) => entry.event.order);
    const locationId = pc.locationId;
    // NOTE: Don't know why this location map is used here, but keeping it for now to not break anything
    const location = locationById.get(locationId);
    const locationName = location?.name;
    const portCallEntry = arrivalEventTd?.event.entry;

    if (!location || !locationName || !portCallEntry || !arrivalEventTd) {
      continue;
    }

    // Port costs are ignored for canal transit port calls
    if (arrivalEventTd.event.isCanalTransitPortCall) {
      continue;
    }

    const sources = getPortCallCostSources(
      portCallEntry,
      orders,
      pc.id,
      locationId,
      locationName,
      vessel,
      portCostEstimateMaps,
      portCostByVcMaps,
      portCostByCoaMaps,
      portCostMarcuraMaps,
      portCostPredictions,
      baseCurrency,
    );

    const numShipments = shipmentsByPortCallId.get(pc.id).size;

    // Skip set port cost for free events (which have no numShipments)
    if (!numShipments) {
      continue;
    }

    const params = mapExternalPortCostEstimatesToParams(
      sources.historicalValue,
      currencyRates,
    );

    costs.set(pc.id, [
      {
        type: 'PORT_CALL',
        name: 'Port cost',
        value:
          (sources.coaPortCost?.value ?? sources.portCostEstimate ?? 0) /
          numShipments,
        numShipments,
        params,
        locations: location ? [location] : [],
        options: {
          ...sources,
          portCallEntry,
        },
      },
    ]);
  }

  return costs;
}

export type PortCallCostSources = {
  coaPortCost: { value: number; currency: string } | undefined;
  defaultValue: DefaultPortCostEstimates | undefined;
  historicalValue: HistoricalPortCostEstimates | undefined;
  latestHistoricalValue: LatestHistoricalPortCostEstimates | undefined;
  integrationCost:
    | { value: number; currency: string; source?: 'PortLog' }
    | undefined;
  oneTimeOverrideCost: number | null;
  portCostDefaultId: number | null;
  portCostEstimate: number | undefined;
  predictionCost: { value: number; params: CostParam[] } | undefined;
  selectedId: number | undefined;
  useHistoricalPortCost: boolean;
  useLatestHistoricalPortCost: boolean;
  useIntegrationPortCost: boolean;
  useOneTimeOverrideCost: boolean;
};

/** Returns all potential port cost source values for a given port call */
export function getPortCallCostSources(
  portCallEventOrEntry: Pick<
    PortCallEntry,
    | 'oneTimeOverrideCost'
    | 'portCostDefaultId'
    | 'useHistoricalPortCost'
    | 'useLatestHistoricalPortCost'
    | 'useIntegrationPortCost'
    | 'useOneTimeOverrideCost'
  >,
  orders: ScenarioOrder[],
  portCallId: ClientId | null,
  portCallLocationId: LocationId,
  portCallLocationName: string,
  vessel: ScenarioTimeline_ShipFragment,
  portCostEstimateMaps: PortCostEstimateMaps,
  portCostByVcMaps: PortCostByVcMaps,
  portCostByCoaMaps: PortCostByCoaMaps | null,
  portCostMarcuraMaps: MarcuraPortCosts,
  portCostPredictions: PortCostPredictionByPortCall | null,
  baseCurrency: Currency,
): PortCallCostSources {
  const {
    byName,
    byPrimaryAttributeId,
    latestByPrimaryAttributeId,
    defaultById,
    defaultByTypeId,
    defaultByVesselId,
    locationIdsByName,
  } = portCostEstimateMaps;

  const defaultValue = getPortCostDefaults(
    portCallLocationId,
    portCallEventOrEntry.portCostDefaultId,
    portCostEstimateMaps,
    vessel,
  );

  const {
    oneTimeOverrideCost,
    useHistoricalPortCost,
    useLatestHistoricalPortCost,
    useOneTimeOverrideCost,
    useIntegrationPortCost,
    portCostDefaultId,
  } = portCallEventOrEntry;

  const vesselId = vessel.id;
  const vesselWeight = vessel.weightCapacity;
  const primaryVesselAttributeId = vessel.primaryAttributeId;

  let selectedId: number | undefined;
  let portCostEstimate: number | undefined;
  let coaPortCost: { value: number; currency: string } | undefined;

  if (portCostByCoaMaps) {
    for (const order of orders) {
      if (!order.coaId) continue;
      const portCostsByVesselId = portCostByCoaMaps
        .get(order.coaId)
        .get(vessel.id)
        .get(portCallLocationId);
      const portCostsByVesselClassId = portCostByCoaMaps
        .get(order.coaId)
        .get(primaryVesselAttributeId ? -primaryVesselAttributeId : null)
        .get(portCallLocationId);
      const portCost = portCostsByVesselId[0] || portCostsByVesselClassId[0];

      if (portCost) {
        coaPortCost = portCost
          ? {
              value: +portCost.cost,
              currency: portCost.coaContractByCoaContractId?.currency ?? '',
            }
          : undefined;
        break;
      }
    }
  }

  if (oneTimeOverrideCost && useOneTimeOverrideCost) {
    portCostEstimate = oneTimeOverrideCost;
  }

  if (!portCostEstimate && portCostDefaultId) {
    portCostEstimate = defaultById.get(portCostDefaultId);
    selectedId = portCostDefaultId;
  }

  const integrationCost = getIntegrationPortCosts(
    portCallLocationId,
    vesselWeight,
    orders,
    portCostByVcMaps,
    portCostMarcuraMaps,
    baseCurrency,
  );

  if (useIntegrationPortCost && integrationCost) {
    portCostEstimate = integrationCost.value;
  }

  if (!portCostEstimate && primaryVesselAttributeId && !useHistoricalPortCost) {
    const defByTypeIdCost = getDefaultPortCostByType(
      defaultByTypeId,
      portCallLocationId,
      primaryVesselAttributeId,
      locationIdsByName,
    );

    if (defByTypeIdCost) {
      selectedId = defByTypeIdCost.id;
      portCostEstimate = defByTypeIdCost.cost;
    }
  }

  const defByNameCost = defaultByVesselId.get(portCallLocationId, vesselId);
  if (!portCostEstimate && defByNameCost) {
    selectedId = defByNameCost.id;
    portCostEstimate = defByNameCost.cost;
  }

  if (!portCostEstimate) {
    const typeIds = vessel?.vesselAttributesAssociationsByShipId.nodes ?? [];
    for (const type of typeIds) {
      const defByTypeIdCost =
        type?.vesselAttributeId &&
        defaultByTypeId.get(portCallLocationId, type.vesselAttributeId);
      if (defByTypeIdCost) {
        selectedId = type.id;
        portCostEstimate = defByTypeIdCost.cost;
        break;
      }
    }
  }

  let historicalValue: HistoricalPortCostEstimates | undefined = undefined;

  if (vessel.name) {
    historicalValue = getHistoricalPortCostEstimates({
      locationName: portCallLocationName,
      vesselName: vessel.name,
      byName,
      primaryVesselAttributeId,
      byPrimaryAttributeId,
    });
  }

  if ((!portCostEstimate || useHistoricalPortCost) && portCallLocationName) {
    portCostEstimate = historicalValue?.cost;
    selectedId = undefined;
  }

  const latestHistoricalValue = getLatestHistoricalPortCost(
    portCallLocationName,
    primaryVesselAttributeId,
    latestByPrimaryAttributeId,
  );

  const portCostPrediction = portCostPredictions?.get(portCallId);
  const predictionCost = portCostPrediction
    ? {
        value: portCostPrediction.value,
        params: portCostPrediction.params,
      }
    : undefined;

  return {
    coaPortCost,
    defaultValue,
    historicalValue,
    latestHistoricalValue,
    integrationCost,
    oneTimeOverrideCost,
    portCostDefaultId,
    portCostEstimate,
    predictionCost,
    selectedId,
    useHistoricalPortCost,
    useLatestHistoricalPortCost,
    useIntegrationPortCost,
    useOneTimeOverrideCost,
  };
}
